import {
  type TOrderSummary,
  type OrderSummaryPaymentDue,
  type OrderSummaryCurrentUser,
  type OrderAddress,
  type OrderSummaryInstallmentSummary,
  type OrderSummaryContactInformation,
} from 'typescript/types/api/solidshop';
import {
  ADDRESS_FIELDS,
  BILLING_PREFIX,
} from 'typescript/schemas/address_forms/address_fields';

export const userIsLoggedIn = (currentUser?: OrderSummaryCurrentUser) => {
  return currentUser?.is_logged_in;
};

export const orderIsWarranty = (orderData: TOrderSummary) => {
  return orderData?.is_warranty === true;
};

/**
 * Serializer-provided OrderAdapter#has_subscription?. NOTE: software_summary is
 * not a proxy — it is empty for bundle/payment-processing subscription orders.
 */
export const orderHasSubscription = (orderData?: TOrderSummary) => {
  return Boolean(orderData?.has_subscription);
};

export const orderRequiresCardOnFile = (orderData?: TOrderSummary) => {
  return Boolean(orderData?.requires_card_on_file);
};

export const orderIsPayInFull = (payment_due?: OrderSummaryPaymentDue) => {
  return payment_due?.payment_schedule === 'upfront';
};

export const orderHasNoSoftwareProducts = (
  softwareSummary?: Record<string, unknown>,
) => {
  if (softwareSummary) {
    return Object.keys(softwareSummary).length === 0;
  }
  return false;
};

/**
 * @param shipAddress - The shipping address of the order
 * @returns boolean - True if the order has a shipping address, false otherwise
 */
export const hasShipAddress = (shipAddress?: OrderAddress) => {
  if (!shipAddress) {
    return false;
  }

  return shipAddress && Object.keys(shipAddress).length > 0;
};

export const orderApplicationFlowToken = (
  installmentSummary?: OrderSummaryInstallmentSummary,
) => {
  return installmentSummary?.application_flow_token;
};

// TODO: Order of address fields will need to be localized for different stores. See AddressSummary.
export const addressString = (address: OrderAddress | undefined) => {
  if (!address) {
    return '';
  }

  const addressLine2String = address.address_line_2
    ? `, ${address.address_line_2}`
    : '';

  return `${address.address_line_1}${addressLine2String}, ${address.locality}, ${address.administrative_district_level_1}, ${address.postal_code}`;
};

export const addressNameString = (
  address: OrderAddress | undefined,
  contactInfo: OrderSummaryContactInformation | undefined,
) => {
  if (!address || !contactInfo) {
    return '';
  }

  return `${address?.first_name} ${address?.last_name}, ${contactInfo.email}, ${contactInfo.phone}`;
};

export const billingAddressFromOrder = (
  address: OrderAddress,
  contactInfo: OrderSummaryContactInformation,
) => {
  return {
    [`${BILLING_PREFIX}${ADDRESS_FIELDS.first_name}`]: address.first_name,
    [`${BILLING_PREFIX}${ADDRESS_FIELDS.last_name}`]: address.last_name,
    [`${BILLING_PREFIX}${ADDRESS_FIELDS.phone}`]: contactInfo.phone,
    [`${BILLING_PREFIX}${ADDRESS_FIELDS.address_line_1}`]:
      address.address_line_1,
    [`${BILLING_PREFIX}${ADDRESS_FIELDS.address_line_2}`]:
      address.address_line_2,
    [`${BILLING_PREFIX}${ADDRESS_FIELDS.locality}`]: address.locality,
    [`${BILLING_PREFIX}${ADDRESS_FIELDS.company}`]: address.company,
    [`${BILLING_PREFIX}${ADDRESS_FIELDS.postal_code}`]: address.postal_code,
    [`${BILLING_PREFIX}${ADDRESS_FIELDS.administrative_district_level_1}`]:
      address.administrative_district_level_1,
  };
};

/**
 * @param paymentDue - OrderSummaryPaymentDue object
 * @returns boolean - True if the order is free, false otherwise
 */
export const isOrderFree = (paymentDue: OrderSummaryPaymentDue) => {
  // if paymentDue is undefined, it will return false
  return Number.parseFloat(paymentDue?.amount) === 0;
};
